import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar/dist/cjs/Calendar.js';
import 'react-calendar/dist/Calendar.css';

import {
    Box,
    Button,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
    TextField,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    ToggleButtonGroup,
    ToggleButton,
    Select,
    MenuItem,
} from '@mui/material';

import axios from 'axios';
import debounce from 'lodash.debounce';

const SubAdminManager = () => {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [userExists, setUserExists] = useState(null);
    const [showRegisterFields, setShowRegisterFields] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        usertype: 'individual',
        email: '',
        userId: '',
        role: ''
    });

    const debouncedPhoneCheck = debounce(async (number) => {

        if (number.startsWith("+91")) {
            number = number.replace(/^\+91\s?/, "");
        }

        setIsLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/admin/users/checkuser?phone=${number}`
            );
            const data = await response.json();
            setUserExists(data.exists);
            if (data.exists) {
                setFormData((prev) => ({ ...prev, userId: data.userId }));
                setFormData((prev) => ({ ...prev, username: data.username }));
                setFormData((prev) => ({ ...prev, role: data.userrole }));
                setFormData((prev) => ({ ...prev, usertype: data.usertype }));
            }
        } catch (error) {
            console.error('Error checking user:', error);
            setUserExists(false);
            alert('Failed to check user. Please try again.');
        } finally {
            setIsLoading(false);
        }

    }, 500);

    const handlePhoneNumberChange = (e) => {
        let input = e.target.value;
        setPhoneNumber(input);
        setUserExists(null);
        setShowRegisterFields(false);
        debouncedPhoneCheck(input);
        setFormData({
            username: '',
            usertype: 'individual',
            email: '',
            userId: '',
            role: ''
        });
        console.log(input);
    };

    const handleRegisterUser = async () => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/user/register`,
                {
                    username: formData.username,
                    usertype: formData.usertype,
                    phone: phoneNumber,
                    email: formData.email,
                    password: 'defaultPassword123',
                }
            );

            if (response.data.success) {
                console.log(response.data.user._id)
                setFormData((prev) => ({ ...prev, userId: response.data.user.id }));
                setFormData((prev) => ({ ...prev, usertype: response.data.user.usertype }));
                setFormData((prev) => ({ ...prev, role: response.data.user.role }));
                debouncedPhoneCheck(phoneNumber);
                setDialogOpen(true);
                setShowRegisterFields(false);
            }
        } catch (error) {
            console.error('Error registering user:', error);
            alert('Failed to register user. Please ensure all details are correct.');
        }
    };

    const handleCreateSubadmin = async () => {
        try {
            const response = await axios.put(`${process.env.REACT_APP_API_URL}/admin/make-subadmin`, { userId: formData.userId });

            if (response.data) {
                alert('User has been assigned as sub-admin');
                setPhoneNumber('');
                setUserExists(null);
                setFormData({
                    username: '',
                    usertype: 'individual',
                    email: '',
                    userId: '',
                    role: ''
                });
                setShowRegisterFields(false);
            }
        } catch (error) {
            console.error(error);
            alert('Failed to assign sub admin to this user.')
        }
    }

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleShowRegisterFields = () => {
        setShowRegisterFields(true);
    };

    const handleUserTypeChange = (event, newUserType) => {
        if (newUserType !== null) {
            setFormData((prev) => ({ ...prev, usertype: newUserType }));
        }
    };

    return (
        <Box sx={{ maxWidth: 700, margin: 'auto', padding: 4 }}>
            <Typography variant="h4" align="center" gutterBottom>
                Create Sub Admin
            </Typography>
            <Box mt={4}>
                <TextField
                    fullWidth
                    label="Phone Number"
                    name="phoneNumber"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    margin="normal"
                    InputProps={{
                        endAdornment: isLoading && <CircularProgress size={20} />,
                    }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                    {userExists === false && !showRegisterFields && (
                        <Typography color="error">
                            User doesn't exist
                        </Typography>
                    )}
                    {userExists === false && !showRegisterFields && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleShowRegisterFields}
                        >
                            Register User?
                        </Button>
                    )}
                </Box>

                {showRegisterFields && (
                    <>
                        <ToggleButtonGroup
                            color="primary"
                            value={formData.usertype}
                            exclusive
                            onChange={handleUserTypeChange}
                            aria-label="User Type"
                        >
                            <ToggleButton value="individual">INDIVIDUAL</ToggleButton>
                            <ToggleButton value="business">BUSINESS</ToggleButton>
                        </ToggleButtonGroup>
                        <TextField
                            fullWidth
                            label="Username"
                            name="username"
                            value={formData.username}
                            onChange={handleFormChange}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Email (Optional)"
                            name="email"
                            value={formData.email}
                            onChange={handleFormChange}
                            margin="normal"
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleRegisterUser}
                            sx={{ mt: 2 }}
                        >
                            Register
                        </Button>
                    </>
                )}
                {userExists && (
                    <Box mt={4}>
                        <Box mb={2}>
                            <Typography variant="subtitle2" color="textSecondary">Username</Typography>
                            <Typography variant="body1">
                                {formData.username} (<span style={{ color: 'green' }}>{formData.role} user</span>)
                            </Typography>

                        </Box>

                        <Box mb={2}>
                            <Typography variant="subtitle2" color="textSecondary">User Type</Typography>
                            <Typography variant="body1">{formData.usertype || 'N/A'}</Typography>
                        </Box>

                        <Box mb={2}>
                            <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                            <Typography variant="body1">{formData.email || '*Not Specified*'}</Typography>
                        </Box>

                        <Box mb={2}>
                            <Typography variant="subtitle2" color="textSecondary">User ID</Typography>
                            <Typography variant="body1">{formData.userId || 'N/A'}</Typography>
                        </Box>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleCreateSubadmin}
                            sx={{ mt: 3, ml: -1 }}
                        >
                            Make sub-admin ?
                        </Button>
                    </Box>


                )}
            </Box>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>User Registered</DialogTitle>
                <DialogContent>
                    <DialogContentText>User registered successfully!</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SubAdminManager;
